/**
 * Service Acts list (2026-06-03): Акти виконаних робіт/послуг.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { serviceActs, type ServiceActListItem } from '@/api/serviceActs';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';


const fmt2 = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function ServiceActsPage() {
  // Стандартні фільтри списків (еталон /employees).
  const f = useDocListFilters();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['service-acts', ...f.queryKey],
    queryFn: () => serviceActs.list({
      limit: 200,
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
  });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);

  const cols = useMemo<ColumnDef<ServiceActListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'contractor_id', header: 'Замовник',
      sortValue: (r) => contractorName.get(r.contractor_id) ?? '',
      cell: (r) => contractorName.get(r.contractor_id) ?? '',
    },
    {
      key: 'total_amount', header: 'Сума', width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'state', header: 'Стан', width: 110,
      sortValue: (r) => r.state ?? '',
      cell: (r) => {
        const v = r.state;
        const label = v === 'POSTED' ? '✓ Проведено' : v === 'CANCELLED' ? '✗ Скасовано' : 'Чернетка';
        const style = v === 'POSTED'
          ? { color: 'var(--badge-green-fg)', fontWeight: 600 }
          : { color: 'var(--text-secondary)', fontWeight: 400 };
        return <span style={style}>{label}</span>;
      },
    },
  ], [contractorName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Акти виконаних робіт/послуг</h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/service-acts/new')}>
          + Новий акт
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <DataTable<ServiceActListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/service-acts/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}
